import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import { format, parseISO } from "date-fns";

import { Icon } from "aible";
import Tooltip from "components/Inputs/Tooltip";

import * as viewReducer from "containers/View/reducer";
import * as reportReducer from "containers/Report/ReportReducer";
import * as globalReducer from "containers/App/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";

import ExploreIcon from "images/HeaderTabs/explore.svg";
import OptimizeIcon from "images/HeaderTabs/optimize.svg";

import { AutoSizer, Column, SortDirection, Table } from "react-virtualized";
import styles from "@/components/LanguageModelArtifact/style.module.css";

import { Button } from "@/aible";
import { Badge } from "@/aible/Badge/Badge";

export const getProjectFocus = (item) => {
  const hasExplore =
    item?.metadata?.explorations?.[0]?.hexMap !== undefined &&
    Object.keys(item?.metadata?.explorations?.[0]?.hexMap ?? {})?.length > 0;
  const hasOptimize =
    item?.metadata?.explorations?.[0]?.projects !== undefined &&
    Object.keys(item?.metadata?.explorations?.[0]?.projects ?? {})?.length > 0;

  let projectFocus = undefined;
  if (hasExplore) {
    projectFocus = "explore";
  }
  if (hasOptimize) {
    projectFocus = "optimize";
  }
  if (item?.metadata?.chatGPTConvo?.chatType) {
    if (item?.metadata?.chatGPTConvo?.chatType === "WHATS_CHANGED") {
      projectFocus = "whatsChanged";
    }
    if (item?.metadata?.chatGPTConvo?.chatType === "KPI_ANALYSIS") {
      projectFocus = "whatsChangedOverview";
    }
    if (item?.metadata?.chatGPTConvo?.chatType === "DOCUMENT") {
      projectFocus = "documentSummarization";
    }
    if (item?.metadata?.chatGPTConvo?.chatType === "SQL") {
      projectFocus = "NLQ";
    }
    if (item?.metadata?.chatGPTConvo?.chatType === "CHAT") {
      projectFocus = "chat";
    }
    return projectFocus;
  }
};

const getProjectFocusTitle = (projectFocus) => {
  if (projectFocus === "explore") {
    // Just different naming
    return "Agentic Analytics";
  }
  if (projectFocus === "optimize") {
    return "Optimize";
  }
  if (projectFocus === "whatsChanged") {
    return "Whats Changed Deep Dive";
  }
  if (projectFocus === "whatsChangedOverview") {
    return "Whats Changed Overview";
  }
  if (projectFocus === "documentSummarization") {
    return "Document Summarization";
  }
  if (projectFocus === "NLQ") {
    return "Data Query (NLQ)";
  }
  if (projectFocus === "chat") {
    return "Chat Only";
  }
};

const ProjectDataTable = ({ data, columns, history, handleDelete }) => {
  const [sortBy, updateSortBy] = useState("createdAt");
  const [sortDirection, updateSortDirection] = useState(SortDirection.DESC);
  const [sortedData, updateSortedData] = useState(data);

  const compareStrings = (a, b) => {
    return a.localeCompare(b);
  };

  const compareNumbers = (a, b) => {
    return a < b ? -1 : a > b ? 1 : 0;
  };

  const compareDates = (a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA < dateB ? -1 : 1;
  };

  const sort = ({ sortBy, sortDirection }) => {
    const serializedData = data.map((item) => {
      const projectFocus = getProjectFocus(item);

      return {
        dataset: item.dataset,
        projectId: item.id,
        createdBy: item["created_by"]?.fullname || "Unknown",
        createdAt: item["created_date"],
        projectName: item?.metadata?.explorations?.[0]?.reportLabel || item.name || "Unknown",
        datasetLabel: item.dataset?.label || "-",
        datasetId: item.dataset?.id ?? item.datasetId,
        outcome: item.dataset?.outcome || "-",
        projectFocus
      };
    });

    const sortedArray = [...serializedData].sort((a, b) => {
      const aValue = Array.isArray(a[sortBy]) ? a[sortBy][0] : a[sortBy];
      const bValue = Array.isArray(b[sortBy]) ? b[sortBy][0] : b[sortBy];

      if (aValue === undefined) return sortDirection === SortDirection.ASC ? 1 : -1;
      if (bValue === undefined) return sortDirection === SortDirection.ASC ? -1 : 1;

      if (["projectId", "datasetId"].includes(sortBy)) {
        return sortDirection === SortDirection.ASC ? compareNumbers(aValue, bValue) : compareNumbers(bValue, aValue);
      }

      if (sortBy === "createdAt") {
        return sortDirection === SortDirection.ASC ? compareDates(aValue, bValue) : compareDates(bValue, aValue);
      }

      return sortDirection === SortDirection.ASC ? compareStrings(aValue, bValue) : compareStrings(bValue, aValue);
    });

    updateSortBy(sortBy);
    updateSortDirection(sortDirection);
    return updateSortedData(sortedArray ?? []);
  };

  useEffect(() => {
    sort({ sortBy, sortDirection });
  }, [data]);

  const rowHeight = 44;
  const tableHeight = data.length * rowHeight + 30;

  const handleRowClick = ({ rowData }) => {
    let urlSlug = "new";
    if (rowData.dataset && rowData.dataset?.id) {
      urlSlug = rowData.dataset.id;
    } else if (rowData.datasetId) {
      urlSlug = rowData.datasetId;
    }
    history.push(`/next/${urlSlug}/${rowData.projectId}`);
  };

  return (
    <div className={styles.fineTuningTableContainer}>
      <div className={styles.dataTableWrapper}>
        <AutoSizer disableHeight={true}>
          {({ width }) => (
            <Table
              width={width}
              height={tableHeight}
              headerHeight={30}
              rowHeight={rowHeight}
              rowCount={sortedData?.length}
              rowGetter={({ index }) => sortedData[index]}
              sort={sort}
              sortBy={sortBy}
              sortDirection={sortDirection}
              rowClassName={styles.fineTuningRow}
              className={styles.fineTuningTable}
              headerClassName={styles.fineTuningHeaderRow}
              onRowClick={handleRowClick}
            >
              {columns.map((column, index) => {
                if (column.dataKey === "projectFocus") {
                  return (
                    <Column
                      key={index}
                      label={column.label}
                      dataKey={column.dataKey}
                      width={column.width}
                      flexGrow={column.flexGrow}
                      className={styles.fineTuningItem}
                      cellRenderer={({ rowData }) => {
                        if (!rowData.projectFocus) {
                          return;
                        }
                        return (
                          <Badge
                            variant="outline"
                            className="rounded-2xl color-[#202939] px-2.5 py-[5px] border-[#dcdcdc] text-[13px] bg-white"
                          >
                            {getProjectFocusTitle(rowData.projectFocus)}
                          </Badge>
                        );
                      }}
                    />
                  );
                }
                if (column.dataKey === "createdAt") {
                  return (
                    <Column
                      key={index}
                      label={column.label}
                      dataKey={column.dataKey}
                      width={column.width}
                      cellRenderer={({ cellData }) => format(parseISO(cellData + "Z"), "MM/dd/yyyy, hh:mm aaa")}
                      className={styles.fineTuningSettings}
                    />
                  );
                }
                if (column.dataKey === "actions") {
                  return (
                    <Column
                      key={index}
                      label={column.label}
                      dataKey={column.dataKey}
                      width={column.width}
                      className={styles.fineTuningSettings}
                      cellRenderer={({ rowData }) => (
                        <div className={styles.fineTuningItemActions}>
                          <Button
                            isSmall
                            onClick={(e) => {
                              console.log(rowData);
                              e.stopPropagation();
                              handleDelete(rowData?.projectId);
                            }}
                            style={{ padding: "5px 7px" }}
                          >
                            {"Delete"}
                          </Button>
                        </div>
                      )}
                    />
                  );
                }
                return (
                  <Column
                    key={index}
                    label={column.label}
                    dataKey={column.dataKey}
                    width={column.width}
                    flexGrow={column.flexGrow}
                    className={styles.fineTuningItem}
                  />
                );
              })}
            </Table>
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

class ExplorationViewContainer extends React.Component {
  getHeaders() {
    return ["Project", "Creation", "Contributors", "Subgroups", "Created By"];
  }

  render() {
    const { data, deleteReport, history, isReportsLoading, fetchNext } = this.props;
    const datasetColumn = [
      { label: "ID", dataKey: "projectId", width: 100 },
      { label: "name", dataKey: "projectName", width: 400 },
      { label: "type", dataKey: "projectFocus", width: 200 },
      { label: "Outcome", dataKey: "outcome", width: 200 },
      {
        label: "Dataset or Document label",
        dataKey: "datasetLabel",
        width: 300
      },
      { label: "Analysis ID", dataKey: "datasetId", width: 100 },
      { label: "Created At", dataKey: "createdAt", width: 150 },
      { label: "Created by", dataKey: "createdBy", width: 200 },
      { label: "", dataKey: "actions", width: 70 }
    ];

    return (
      <div style={{ backgroundColor: "white", padding: "20px 0" }}>
        <ProjectDataTable data={data} columns={datasetColumn} history={history} handleDelete={deleteReport} />
        <div style={{ display: "flex", justifyContent: "center", padding: "0 10px" }}>
          <Button onClick={() => fetchNext()} isLoading={isReportsLoading} disabled={isReportsLoading}>
            Load More
          </Button>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchNext: () => dispatch(reportReducer.fetchNextReports()),
  fetchAll: () => dispatch(reportReducer.fetchAllReports()),
  deleteReport: (id) => dispatch(reportReducer.deleteReport(id)),
  handleMoveReport: (reportId) => dispatch(foldersReducer.handleMoveReport(reportId)),
  moveReportFolder: (reportId, folderId) => dispatch(foldersReducer.moveReportFolder(reportId, folderId)),

  handleExplorationPin: (id) => dispatch(viewReducer.updateExplorationPin(id)),
  getChatMetrics: (convArr) => dispatch((dispatch, getState) => chatGPTReducer.getChatMetrics(getState(), convArr))
});

const mapStateToProps = (state, ownProps) => {
  return {
    data: viewReducer.getReports(state),
    isTile: viewReducer.getIsTile(state),
    isReportsLoading: globalReducer.getIsReportsLoading(state),
    type: viewReducer.getType(state),
    selectedFolder: foldersReducer.getSelectedFolder(state),

    isTableauMode: globalReducer.getIsTableauMode(state),

    pinnedExplorations: viewReducer.getPinnedExplorations(state),

    lookerSDK: loginReducer.getLookerAPIClient(state)
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect, withRouter)(ExplorationViewContainer);
